const API_BASE = '/api/compliance/controls';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface Control {
  id: number;
  company_id: number;
  name: string;
  description?: string;
  control_ref: string;
  framework_count?: number;
  evidence_count?: number;
  created_at: string;
}

export const controlsApi = {
  async list(params?: { page?: number; limit?: number; company_id?: number }): Promise<{ controls: Control[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.company_id) q.set('company_id', String(params.company_id));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async create(data: { company_id: number; name: string; description?: string; control_ref: string }): Promise<Control> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async get(id: number): Promise<Control> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async update(id: number, data: Partial<Control>): Promise<Control> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async delete(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async mapEvidence(id: number, evidenceItemId: number, notes?: string): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/evidence`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ evidence_item_id: evidenceItemId, mapping_notes: notes }) });
    await handleResponse(res);
  },

  async unmapEvidence(id: number, evidenceId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/evidence/${evidenceId}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async mapToPage(id: number, compliancePageId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/pages`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ compliance_page_id: compliancePageId }) });
    await handleResponse(res);
  },

  async unmapFromPage(id: number, pageId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/pages/${pageId}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async getCrossFramework(id: number): Promise<any> {
    const res = await fetch(`${API_BASE}/${id}/cross-framework`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(companyId?: number): Promise<{ total: number; with_evidence: number; cross_framework_avg: number }> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
